import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import PortalLayout from '../components/common/PortalLayout';
import MyAccount from '../pages/portal/MyAccount';
import Logs from '../pages/portal/Logs';
import DataVaultRouting from '../pages/portal/configurations/DataVault/DataVaultRouting';
import Keys from '../pages/portal/configurations/Keys';
import Reports from '../pages/portal/Reports';
import AnalyticsHub from '../pages/portal/AnalyticsHub';
import ReportDesignerPage from '../pages/portal/chatagents/new/Reports/ReportDesignerPage';
import LLMRouting from '../pages/portal/configurations/LLMRouting';
import AccessSettings from '../pages/portal/configurations/AccessSettings';
import WorkflowBuilder from '../pages/portal/workflowbuilder/flowbuilder';
import FlowBuilderConsole from '../pages/portal/workflowbuilder/flowBuilderConsole';
import WorkflowEditor from '../pages/portal/workflowbuilder/flowBuilderConsole';
import Deployments from '../pages/portal/configurations/Deployment';
import Dashboard from '../pages/portal/Dashboard/Dashboard';
import Agents from '../pages/portal/configurations/agents/Agents';
import IngestionList from '../pages/portal/configurations/ingestion/Data-Ingestion';
import KnowledgeBaseList from '../pages/portal/configurations/KnowledgeBase/KnowledgeBaseList';
import DataSourceListingPage from '../pages/portal/configurations/KnowledgeBase/KnowledgeBaseList';
import IngestionSchedules from '../pages/portal/configurations/ingestion/IngestionSchedules';
import SystemSettings from '../pages/portal/configurations/Settings/SystemSettings';
import IntentModelListing from '../pages/portal/Intent/IntentModelListing';
import PrivateRoute from './PrivateRoute';
import NoAccessPage from '../pages/portal/NoAccessPage';
import AgentStudio from '../pages/portal/configurations/agents/AgentStudio/AgentStudio';

import ApplicationListing from '../pages/portal/applications/components/ApplicationListings';
import CatalogListing from '../pages/portal/catalog';
import AiModelsListing from '../pages/portal/ai-models';
import AiModelRegisterScreen from '../pages/portal/ai-models/AiModelRegisterScreen';
import ChatAgents from '../pages/portal/chatagents/new/ChatAgents';
import ChatAgentGenerator from '../pages/portal/chatagents/new/ChatAgentsLayout';
import ChatbotIntentsListing from '../pages/portal/chatagents/new/ChatIntents/ChatbotIntentsListing';
import IntentDefinitionPage from '../pages/portal/chatagents/new/ChatIntents/IntentDefinitionPage';
import ConversationGraphBuilder from '../pages/portal/chatagents/new/ChatIntents/ConversationGraphBuilder';
import ConnectionsPage from '../pages/portal/Connections/ConnectionsPage';
import MCPResourcesV2 from '../pages/portal/MCP/MCPResources/MCPResourcesV2/MCPResourcesV2';
import MCPLiveMonitor from '../pages/portal/MCP/LiveMonitor/LiveMonitor';
import GovernedMcpGatewayScreen, {
  GatewayAiUsagePage,
  GatewayConfigurationPage,
  GatewayDashboardPage,
  GatewayToolsPage,
  GovernedMcpGatewayIndexRedirect,
} from '../pages/portal/governed-mcp-gateway/GovernedMcpGatewayScreen';
import AIGovernanceLayout from '../pages/portal/ai-governance/AIGovernanceLayout';
import PoliciesListing from '../pages/portal/ai-governance/Policies/PoliciesListing';
import PolicyCreateForm from '../pages/portal/ai-governance/Policies/PolicyCreateForm';
import PolicyGroupsScreen from '../pages/portal/ai-governance/PolicyGroups/PolicyGroupsScreen';
import AssignmentsScreen from '../pages/portal/ai-governance/Assignments/AssignmentsScreen';
import OverridesScreen from '../pages/portal/ai-governance/Overrides/OverridesScreen';
import PolicyLibraryScreen from '../pages/portal/ai-governance/PolicyLibrary/PolicyLibraryScreen';
import AuditLogsScreen from '../pages/portal/ai-governance/AuditLogs/AuditLogsScreen';
import GovernanceSettingsScreen from '../pages/portal/ai-governance/Settings/GovernanceSettingsScreen';
import OrchestrationV2 from '../pages/portal/workflows/OrchestrationV2';
import { buildPortalOrchestrationHref } from '../pages/portal/workflows/orchestrationFoUrl';
import { useTheme } from '../components/common/Theme/ThemeContext';
import HealthStatusPage from '../pages/portal/health/HealthStatusPage';

const wrapAdmin = (element: React.ReactElement) => (
  <PrivateRoute>{element}</PrivateRoute>
);

function LegacyAnalyticsHubRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/portal/analytics-hub${search}`} replace />;
}

function LegacyWorkflowsRedirect() {
  const { theme } = useTheme();
  return <Navigate to={buildPortalOrchestrationHref(theme)} replace />;
}

const portalRoutes: RouteObject = {
  path: '/portal',
  element: wrapAdmin(<PortalLayout />),
  children: [
    { path: 'workflows', element: <LegacyWorkflowsRedirect /> },
    { path: 'orchestration/*', element: wrapAdmin(<OrchestrationV2 />) },
    { path: 'reports', element: wrapAdmin(<Reports />) },
    { path: 'analytics-hub', element: wrapAdmin(<AnalyticsHub />) },
    { path: 'analytics-hub/designer/new', element: wrapAdmin(<ReportDesignerPage />) },
    { path: 'analytics-hub/designer/:reportId', element: wrapAdmin(<ReportDesignerPage />) },
    { path: 'reports-builder', element: wrapAdmin(<LegacyAnalyticsHubRedirect />) },
    {
      path: 'configurations',
      children: [
        { path: 'keys', element: wrapAdmin(<Keys />) },
        { path: 'data-ingestion', element: wrapAdmin(<IngestionList />) },
        { path: 'llm-routing', element: wrapAdmin(<LLMRouting />) },
        { path: 'data-vault', element: wrapAdmin(<DataVaultRouting />) },
        { path: 'schedules', element: wrapAdmin(<IngestionSchedules />) }
      ],
    },
    { path: 'knowledge-base/:workspaceId/:kbId', element: wrapAdmin(<KnowledgeBaseList />) },
    { path: 'knowledge-base/:workspaceId', element: wrapAdmin(<KnowledgeBaseList />) },
    { path: 'knowledge-base', element: wrapAdmin(<KnowledgeBaseList />) },
    { path: 'logs', element: wrapAdmin(<Logs />) },
    { path: 'health-status', element: wrapAdmin(<HealthStatusPage />) },
    { path: 'no-access', element: <PrivateRoute><NoAccessPage /></PrivateRoute> },
    { path: 'my-account', element: wrapAdmin(<MyAccount />) },
    { path: 'access-settings', element: <PrivateRoute requireAdmin><AccessSettings /></PrivateRoute> },
    { path: 'connections', element: wrapAdmin(<ConnectionsPage />) },
    { path: 'flowbuilder/:workflowId', element: wrapAdmin(<WorkflowEditor />) },
    { path: 'flowbuilder', element: wrapAdmin(<WorkflowEditor />) },
    { path: 'deployments', element: wrapAdmin(<Deployments />) },
    { path: 'agents', element: wrapAdmin(<Agents />) },
    { path: 'home', element: wrapAdmin(<Dashboard />) },
    { path: 'system-settings', element: wrapAdmin(<SystemSettings />) },
    { path: 'agentStudio', element: wrapAdmin(<AgentStudio />) },
    { path: 'applications', element: wrapAdmin(<ApplicationListing />) },
    { path: 'catalog', element: wrapAdmin(<CatalogListing />) },
    { path: 'ai-models/register', element: wrapAdmin(<AiModelRegisterScreen />) },
    { path: 'ai-models/:modelId/edit', element: wrapAdmin(<AiModelRegisterScreen />) },
    { path: 'ai-models', element: wrapAdmin(<AiModelsListing />) },
    { path: 'intent-orchestration', element: wrapAdmin(<IntentModelListing />) },
    { path: 'chatagent', element: wrapAdmin(<ChatAgents />) },
    { path: 'chatbot/:chatbotId/intents/new', element: wrapAdmin(<IntentDefinitionPage />) },
    { path: 'chatbot/:chatbotId/intents/:intentId/edit', element: wrapAdmin(<IntentDefinitionPage />) },
    { path: 'createchatagent/intents/new', element: wrapAdmin(<IntentDefinitionPage />) },
    { path: 'createchatagent/intents/:intentId/edit', element: wrapAdmin(<IntentDefinitionPage />) },
    { path: 'createchatagent', element: wrapAdmin(<ChatAgentGenerator />), children: [
      { path: 'intents', element: wrapAdmin(<ChatbotIntentsListing />) },
    ] },
    { path: 'mcpresources', element: wrapAdmin(<MCPResourcesV2 />) },
    { path: 'mcpresources-v2', element: <Navigate to="/portal/mcpresources" replace /> },
    { path: 'mcplivemonitor', element: wrapAdmin(<MCPLiveMonitor />) },
    {
      path: 'governed-mcp-gateway',
      element: wrapAdmin(<GovernedMcpGatewayScreen />),
      children: [
        { index: true, element: <GovernedMcpGatewayIndexRedirect /> },
        { path: 'dashboard', element: <GatewayDashboardPage /> },
        { path: 'tools', element: <GatewayToolsPage /> },
        { path: 'configuration', element: <GatewayConfigurationPage /> },
        { path: 'ai-usage', element: <GatewayAiUsagePage /> },
      ],
    },
    {
      path: 'ai-governance',
      element: wrapAdmin(<AIGovernanceLayout />),
      children: [
        { index: true, element: <Navigate to="policies" replace /> },
        { path: 'policies', element: wrapAdmin(<PoliciesListing />) },
        { path: 'policies/create', element: wrapAdmin(<PolicyCreateForm />) },
        { path: 'policies/:policyId/edit', element: wrapAdmin(<PolicyCreateForm />) },
        { path: 'groups', element: wrapAdmin(<PolicyGroupsScreen />) },
        { path: 'assignments', element: wrapAdmin(<AssignmentsScreen />) },
        { path: 'overrides', element: wrapAdmin(<OverridesScreen />) },
        { path: 'library', element: wrapAdmin(<PolicyLibraryScreen />) },
        { path: 'audit-logs', element: wrapAdmin(<AuditLogsScreen />) },
        { path: 'settings', element: wrapAdmin(<GovernanceSettingsScreen />) },
      ],
    },
    {
      path: 'chatbot/:chatbotId',
      element: wrapAdmin(<ChatAgentGenerator />),
      children: [
        { path: 'intents', element: wrapAdmin(<ChatbotIntentsListing />) },
      ],
    },
    {
      path: 'agentStudio/:agentId',
      element: wrapAdmin(<AgentStudio />),
    }
  ],
};

export default portalRoutes;
